/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { resolveToastDuration } from '@/utils/toastDuration';

export type ToastSeverity = 'default' | 'success' | 'error';

export interface ToastState {
  open: boolean;
  message: string;
  severity: ToastSeverity;
}

interface ToastContextValue {
  showToast: (
    message: string,
    severity?: ToastSeverity,
    durationMs?: number,
  ) => void;
  hideToast: () => void;
  toast: ToastState;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'default',
  });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hideToast = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setToast((t) => ({ ...t, open: false }));
  }, []);

  const showToast = useCallback(
    (
      message: string,
      severity: ToastSeverity = 'default',
      durationMs?: number,
    ) => {
      const duration = resolveToastDuration(severity, durationMs);
      if (timerRef.current) clearTimeout(timerRef.current);
      setToast({ open: true, message, severity });
      timerRef.current = setTimeout(() => {
        setToast((t) => ({ ...t, open: false }));
        timerRef.current = null;
      }, duration);
    },
    [],
  );

  const value = useMemo(
    () => ({
      showToast,
      hideToast,
      toast,
    }),
    [hideToast, showToast, toast],
  );

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
}

export function useToastContext(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToastContext must be used within ToastProvider');
  }
  return ctx;
}
